import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image as ImageIcon, Link2, Loader2, SlidersHorizontal } from 'lucide-react';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Skeleton } from '../../ui/skeleton';
import { useToast } from '../../ui/use-toast';
import { trackEvent } from '../../../utils/analytics';
import { climbTitle } from '../../../utils/climb-title';
import { climbsService } from '../../../services/climbs';
import { climbShareUrl, useClimbPhotoEffect } from '../climb-photo-effect';
import { drawShareCard, shareCardFile } from './share-card';
import { shareOrDownloadFile } from './share-file';
import { useClimbThumbnail, useWatermarkLogo } from './use-climb-thumbnail';
import type { Climb } from '../../../types/climb';
import type { Board } from '../../../types/device';

/** The preview's width in canvas pixels: about twice the box it is shown in, for a sharp picture. */
const PREVIEW_WIDTH = 640;

interface ShareClimbDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  climb: Climb;
  /** The board a board problem is viewed through, so its card shows the same wall. */
  board?: Board | null;
  /** The climb's own page (no trailing /share): what the link shares. */
  climbPath: string;
  /** "Advanced mode": the full-page editor (`/share/advance/new`) takes over. */
  onOpenAdvanced: () => void;
}

/**
 * Everything a climb can be shared as, opened by the climb page's share button
 * and by its `/share` address — so a link to `/share` hands somebody this
 * dialog straight away. The link itself, the climb's card as a picture, or the
 * advanced mode — a page of its own (`/share/advance/new`) that lays the climb
 * over a photo or a video from the phone.
 */
export function ShareClimbDialog({ open, onOpenChange, climb, ...rest }: ShareClimbDialogProps) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-md" data-testid="share-climb-dialog">
        <DialogHeader>
          <DialogTitle>{t('shareClimb.dialogTitle')}</DialogTitle>
          <DialogDescription>{climbTitle(climb)}</DialogDescription>
        </DialogHeader>
        {/* Its own component so the card's picture is only fetched once the dialog is open. */}
        <ShareClimbDialogBody climb={climb} {...rest} />
      </DialogContent>
    </Dialog>
  );
}

function ShareClimbDialogBody({ climb, board, climbPath, onOpenAdvanced }: Omit<ShareClimbDialogProps, 'open' | 'onOpenChange'>) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const photoEffect = useClimbPhotoEffect(climb);
  const { picture, info, status } = useClimbThumbnail(climb, board);
  const logo = useWatermarkLogo();
  const previewRef = useRef<HTMLCanvasElement>(null);
  // Made as soon as the picture is in, so the share sheet opens inside the tap
  // that asked for it — Safari refuses one that comes after a wait.
  const [cardFile, setCardFile] = useState<File | null>(null);
  const [sharingImage, setSharingImage] = useState(false);

  useEffect(() => {
    if (!picture) return;
    if (previewRef.current) drawShareCard(previewRef.current, picture, info, logo, PREVIEW_WIDTH);

    let active = true;
    const canvas = document.createElement('canvas');
    drawShareCard(canvas, picture, info, logo);
    shareCardFile(canvas, climb.slug).then((file) => { if (active) setCardFile(file); });
    return () => { active = false; };
  }, [picture, info, logo, climb.slug]);

  const shareLink = async () => {
    // With the effect the photo is shown with, so the link's preview is lit too.
    const url = climbShareUrl(`${window.location.origin}${climbPath}`, climb, photoEffect);
    trackEvent('share_link_clicked', { climb_slug: climb.slug });
    try {
      if (navigator.share) {
        await navigator.share({
          title: climb.name || t('climbProfile.share'),
          text: t('climbProfile.shareText', { name: climb.name, difficulty: climb.difficulty }),
          url,
        });
      } else {
        await navigator.clipboard.writeText(url);
        toast({
          title: t('climbProfile.linkCopied'),
          description: t('climbProfile.linkCopiedDescription'),
          duration: 3000,
        });
      }
    } catch (error) {
      // The share sheet dismissed is not a failure.
      if ((error as Error).name !== 'AbortError') {
        console.error('Failed to share:', error);
        toast({
          title: t('errors.shareFailed'),
          description: t('errors.shareFailedDescription'),
          variant: 'destructive',
        });
      }
    }
  };

  const shareImage = async () => {
    if (!cardFile) return;
    trackEvent('share_image_clicked', { climb_slug: climb.slug });
    // Recorded here, as the editor does: the share sheet never says what became of it.
    climbsService.recordShare(climb.id, { template: 'thumbnail', mediaType: 'image' });
    setSharingImage(true);
    try {
      const outcome = await shareOrDownloadFile(cardFile, {
        title: climbTitle(climb),
        text: t('climbProfile.shareText', { name: climb.name, difficulty: climb.difficulty }),
      });
      if (outcome === 'downloaded') toast({ title: t('shareClimb.downloadedFallback') });
    } catch (error) {
      console.error('Failed to share the image:', error);
      toast({ title: t('shareClimb.shareError'), variant: 'destructive' });
    } finally {
      setSharingImage(false);
    }
  };

  const openAdvanced = () => {
    trackEvent('share_advanced_opened', { climb_slug: climb.slug });
    onOpenAdvanced();
  };

  return (
    <div className="flex flex-col gap-4">
      {status === 'loading' && <Skeleton className="mx-auto aspect-[3/4] h-[45dvh] max-w-full rounded-md" />}
      <canvas
        ref={previewRef}
        className={`mx-auto block h-auto max-h-[45dvh] w-auto max-w-full rounded-md shadow ${status === 'ready' ? '' : 'hidden'}`}
        aria-label={t('shareClimb.imagePreview')}
        data-testid="share-climb-card-preview"
      />
      <div className="flex flex-col gap-2">
        <Button variant="outline" className="w-full gap-2" onClick={shareLink} data-testid="share-climb-link">
          <Link2 className="size-4" aria-hidden />
          {t('shareClimb.shareLink')}
        </Button>
        {status !== 'none' && (
          <Button
            variant="outline"
            className="w-full gap-2"
            onClick={shareImage}
            disabled={!cardFile || sharingImage}
            data-testid="share-climb-image"
          >
            {sharingImage || !cardFile
              ? <Loader2 className="size-4 animate-spin" aria-hidden />
              : <ImageIcon className="size-4" aria-hidden />}
            {t('shareClimb.shareImage')}
          </Button>
        )}
        <Button className="w-full gap-2" onClick={openAdvanced} data-testid="share-climb-advanced">
          <SlidersHorizontal className="size-4" aria-hidden />
          {t('shareClimb.advancedMode')}
        </Button>
      </div>
    </div>
  );
}
